import { Box, Container, Typography } from '@mui/material';
import { MuiOtpInput } from 'mui-one-time-password-input';
import { useNavigate } from 'react-router-dom';
import { auth } from 'routes/config';
import { useForm, Controller } from 'react-hook-form';
import { VerifyCodeRequest } from 'apis/auth/types';
import { useSendVerificationCode } from 'apis/auth/hook';
import { getCookie } from 'utils/helperFunctions';
import { CacheKeys } from 'utils/cacheKeys';
import Button from 'components/Common/Button';
import { zodResolver } from '@hookform/resolvers/zod';
import { verifyCodeSchema } from './ValidationSchema';
import { useGetOrganizations } from 'apis/agency/organizations/hook';

export default function EmailVerification() {
  const navigate = useNavigate();
  const { data: organizations } = useGetOrganizations();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<VerifyCodeRequest>({
    resolver: zodResolver(verifyCodeSchema)
  });
  const { mutate: verifyCode, isPending } = useSendVerificationCode();

  const onSubmit = (data: VerifyCodeRequest) => {
    verifyCode(data);
  };

  return (
    <Container
      maxWidth='xs'
      style={{
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          mb: 2
        }}
      >
        <img src={organizations?.logo_url} alt='app-logo' height={72} />
        <Typography variant='h1' fontWeight={500} mt={3}>
          Sign Up / Log In to Your Account
        </Typography>
        <Typography color='secondary' variant='body2'>
          We’ve sent a 6-digit code to your email {getCookie(CacheKeys.userEmail)}
        </Typography>
      </Box>

      <Box
        component='form'
        sx={{ width: '100%', maxWidth: '100%' }}
        noValidate
        autoComplete='off'
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          name='code'
          control={control}
          render={({ field }) => (
            <MuiOtpInput
              {...field}
              value={field.value || ''}
              onChange={(value) => setValue('code', value)}
              length={6}
              autoFocus
              TextFieldsProps={{
                size: 'small',
                error: !!errors.code,
                type: 'tel'
              }}
            />
          )}
        />
        {errors.code && (
          <Typography color='error' variant='body2' sx={{ mt: 1 }}>
            {errors.code.message}
          </Typography>
        )}
        <Button type='submit' variant='contained' fullWidth sx={{ mt: 3 }} isPending={isPending}>
          Verify
        </Button>
        <Button
          color='primary'
          variant='text'
          sx={{ display: 'flex', mx: 'auto', fontWeight: 500 }}
          onClick={() => navigate(`${auth.base}/${auth.login}`)}
        >
          Change Email?
        </Button>
      </Box>
    </Container>
  );
}
